/**
 * AI のアプリ（ChatGPT・Claude など）の「Connect」から回されて来た人のログイン画面（2026-10-31 core/login-from-ai・founder「この案で進めて」）。
 *
 * WHY: ChatGPT のプラグインの出し直しが「サインインまたは OAuth を完了できない」で却下された。審査の人は Connect → /login まで来て、
 *   1 度も送信していない（docs/teams/core/chatgpt-review/findings.md）。頁の上に Google・GitHub・パスキーが並び、
 *   「AI のアプリをつなぐためのログイン」とも書いていなかった。そこで AI から来た時だけ:
 *     1. 見出しの下の 1 行（auth.loginLead）を auth.loginLeadAiConnect に替える（行を足さない = 読む量を増やさない）
 *     2. メール＋パスワードを Google・GitHub・パスキーより上にする（審査のアカウントはパスワードだけで入る）
 *   ふつうのログイン（AI から来ていない）の見た目は 1 つも変えない（loginFromAi.test.ts）。
 *
 * アプリの名前を出さない理由: ログイン前の画面が持っているのは state.from の `/mcp/authorize?request=<ID>` だけ。名前を読む
 *   GET /v1/developers/mcp-authz-requests/:id は本人のセッションが要る（apps/server の mcpAuthzDeveloper.ts の requireGrantSession）
 *   = ログイン前は読めない。推測で「ChatGPT」と出すと Claude から来た人に嘘になるので「your AI app」と書く。
 */
import { afterLoginPath } from './authForms.ts';
import type { LoginMethod } from './lastLogin.ts';

/**
 * ログインの後の行き先が許可の画面（/mcp/authorize）か = AI のアプリの Connect から来た。
 * afterLoginPath を通す: 外部・ログイン画面への戻りは '/' になる（行き先として使えない値で AI の見た目にしない）。
 * `/mcp/authorizex` のような別の道は含めない（? # 終わりの直前まで一致）
 */
export function cameFromAiConnect(from: unknown): boolean {
  return /^\/mcp\/authorize(?:[?#]|$)/.test(afterLoginPath(from));
}

/**
 * メール＋パスワードを Google 等より上に置くか。
 * ふつう = 前回メールで入った端末だけ（2026-09-28 creator/login-redesign の決まりのまま）。AI から来た時は端末にかかわらず上。
 */
export function loginEmailFirst(last: LoginMethod | null, fromAi: boolean): boolean {
  return fromAi || last === 'email';
}

/** 送信のときに読むフォームの値（FormData の get だけ使う = テストでは Map のような物を渡せる） */
type FormValues = { get(name: string): FormDataEntryValue | null | undefined };

/**
 * 送る資格情報。React の状態が空でも、フォームの欄（name="email" / name="password" = authForms.ts）に値が入っていればそれを使う。
 * WHY: 自動の入力の道具（審査の人の道具・一部の拡張）は input の value を直接書き、React の onChange を起こさないことがある。
 *   状態だけを見ると「入れたのに Email or password is incorrect」で API を呼ばない（findings.md の候補 4・直し方 5）。
 *   状態に値があればそれを優先する（ふつうの手入力・パスワード管理は今までどおり）。空の検査は呼ぶ側（AuthPage）がそのまま行う
 */
export function loginCredentials(state: { email: string; password: string }, form: FormValues | null): { email: string; password: string } {
  const field = (name: string) => {
    const v = form?.get(name);
    return typeof v === 'string' ? v : '';
  };
  return {
    email: state.email.trim() ? state.email : field('email'),
    password: state.password ? state.password : field('password')
  };
}
